import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, LogOut, Mail, Sparkles } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import SEO from '../components/SEO';

export default function Account() {
    const { customer, isAuthed, register, login, logout } = useCustomerAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState('register');
    const [form, setForm] = useState({ email: '', password: '', name: '' });
    const [busy, setBusy] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.password) { toast.error('Email and password are required.'); return; }
        setBusy(true);
        toast.dismiss();
        const res = mode === 'register'
            ? await register(form.email, form.password, form.name)
            : await login(form.email, form.password);
        setBusy(false);
        if (res.ok) {
            toast.dismiss();
            toast.success(mode === 'register' ? 'Welcome to by anfal.' : 'Welcome back.');
        } else {
            toast.error(res.error);
        }
    };

    if (isAuthed) {
        return (
            <div className="max-w-lg mx-auto px-4 sm:px-6 py-16" data-testid="account-page">
                <SEO title="My Account" canonical="/account" noIndex />
                <div className="luxury-card p-8 text-center">
                    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center font-serif text-2xl text-white" style={{ backgroundColor: 'var(--brand-burgundy)' }}>
                        {(customer?.name || customer?.email || 'A').charAt(0).toUpperCase()}
                    </div>
                    <h1 className="font-serif text-3xl mt-4" style={{ color: 'var(--brand-burgundy)' }}>{customer?.name || 'Welcome'}</h1>
                    <p className="text-sm mt-1 inline-flex items-center gap-1.5" style={{ color: 'var(--ink-muted)' }}><Mail className="w-4 h-4" /> {customer?.email}</p>
                    <div className="mt-8 grid gap-3">
                        <button onClick={() => navigate('/shop')} data-testid="account-shop-button" className="btn-burgundy px-6 py-3 rounded-xl text-xs tracking-[0.20em] uppercase">Continue Shopping</button>
                        <button onClick={() => { logout(); toast.success('Signed out.'); }} data-testid="account-logout-button" className="btn-outline-burgundy px-6 py-3 rounded-xl text-xs tracking-[0.20em] uppercase inline-flex items-center justify-center gap-2">
                            <LogOut className="w-4 h-4" /> Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-16" data-testid="account-page">
            <SEO title={mode === 'register' ? 'Create Account' : 'Sign In'} canonical="/account" noIndex />
            <div className="text-center mb-8">
                <p className="eyebrow inline-flex items-center gap-1.5" style={{ color: 'var(--brand-burgundy)' }}><Sparkles className="w-4 h-4" /> Members</p>
                <h1 className="font-serif text-4xl md:text-5xl mt-2" style={{ color: 'var(--brand-burgundy)' }}>
                    {mode === 'register' ? 'Join by anfal' : 'Welcome back'}
                </h1>
                <p className="text-sm mt-3" style={{ color: 'var(--ink-muted)' }}>
                    {mode === 'register'
                        ? 'Register with just your email to save your bag, track requests and enjoy a tailored experience.'
                        : 'Sign in to pick up right where you left off.'}
                </p>
            </div>

            <div className="luxury-card p-8">
                <div className="flex items-center gap-1 p-1 rounded-xl bg-[var(--brand-rose)]/50 mb-6">
                    {[['register', 'Register'], ['login', 'Sign In']].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setMode(k)}
                            data-testid={`account-tab-${k}`}
                            className={`flex-1 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.16em] transition-colors ${
                                mode === k ? 'bg-[var(--brand-burgundy)] text-white' : 'text-[var(--brand-burgundy)] hover:bg-white/60'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="account-form">
                    {mode === 'register' && (
                        <div>
                            <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Name (optional)</label>
                            <input data-testid="account-name" className="input-luxury" value={form.name} onChange={(e) => update('name', e.target.value)} />
                        </div>
                    )}
                    <div>
                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Email *</label>
                        <input data-testid="account-email" required type="email" className="input-luxury" value={form.email} onChange={(e) => update('email', e.target.value)} />
                    </div>
                    <div>
                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Password *</label>
                        <input data-testid="account-password" required type="password" minLength={6} className="input-luxury" value={form.password} onChange={(e) => update('password', e.target.value)} />
                    </div>
                    <button type="submit" disabled={busy} data-testid="account-submit" className="btn-burgundy w-full px-6 py-3.5 rounded-xl text-xs tracking-[0.24em] uppercase font-medium disabled:opacity-60">
                        {busy ? 'Please wait...' : (mode === 'register' ? 'Create Account' : 'Sign In')}
                    </button>
                </form>
            </div>
        </div>
    );
}
